import { toast } from 'react-toastify';

/**
 * Notification Service
 * Handles email notifications and in-app notifications
 * Currently using mock implementation - to be replaced with actual email service
 */

// Email notification types
const EMAIL_TYPES = {
  APPLICATION_SUBMITTED: 'application_submitted',
  APPLICATION_APPROVED: 'application_approved',
  APPLICATION_REJECTED: 'application_rejected',
  PANEL_ASSIGNMENT: 'panel_assignment',
  PANEL_STATUS_CHANGE: 'panel_status_change',
  PANEL_DECISION: 'panel_decision',
  SHORTLIST_REVIEW_REQUEST: 'shortlist_review_request',
  DOCUMENT_UPLOADED: 'document_uploaded',
  DEADLINE_REMINDER: 'deadline_reminder'
};

// In-app notification storage key
const NOTIFICATIONS_KEY = 'apex_notifications';

class NotificationService {
  /**
   * Send email notification (mock implementation)
   * In production, this would call an actual email service API
   */
  async sendEmail(to, subject, body, type = 'general', attachments = []) {
    try {
      // Mock API call - in production, replace with actual email service
      console.log('📧 Email Notification:', {
        to,
        subject,
        body,
        type,
        attachments,
        timestamp: new Date().toISOString()
      });

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Store notification locally for demo purposes
      this.storeNotification({
        id: Date.now(),
        type: 'email',
        emailType: type,
        to,
        subject,
        body,
        sentAt: new Date().toISOString(),
        status: 'sent'
      });

      return { success: true, message: 'Email sent successfully' };
    } catch (error) {
      console.error('Email sending failed:', error);
      return { success: false, message: 'Failed to send email', error };
    }
  }

  /**
   * Send application submission confirmation
   */
  async sendApplicationSubmittedEmail(applicationData) {
    const subject = 'Application Submitted - APEX Panel System';
    const body = `
      Dear ${applicationData.applicantName},

      Thank you for submitting your application to join the APEX Cadre.

      Application ID: ${applicationData.applicationId || applicationData.id}
      Submitted: ${new Date(applicationData.submittedDate).toLocaleString()}
      Status: ${applicationData.status}

      We will review your application and notify you of our decision soon.

      Best regards,
      APEX Admin Team
    `;

    const result = await this.sendEmail(
      applicationData.email,
      subject,
      body,
      EMAIL_TYPES.APPLICATION_SUBMITTED
    );

    if (result.success) {
      toast.success('Confirmation email sent to applicant');
    }

    // Also notify admin
    await this.notifyAdminOfNewApplication(applicationData);

    return result;
  }

  /**
   * Notify admin of new application
   */
  async notifyAdminOfNewApplication(applicationData) {
    const subject = 'New Cadre Application Received';
    const body = `
      A new cadre application has been submitted.

      Applicant: ${applicationData.applicantName}
      Email: ${applicationData.email}
      Expertise: ${applicationData.expertise?.join(', ') || 'N/A'}
      Experience: ${applicationData.yearsExperience} years
      Application ID: ${applicationData.applicationId || applicationData.id}

      Please log in to review and process this application.
    `;

    return await this.sendEmail(
      'admin@apex.com',
      subject,
      body,
      EMAIL_TYPES.APPLICATION_SUBMITTED
    );
  }

  /**
   * Send application approval notification
   */
  async sendApplicationApprovedEmail(applicationData) {
    const subject = 'Application Approved - Welcome to APEX Cadre';
    const body = `
      Dear ${applicationData.applicantName},

      Congratulations! Your application to join the APEX Cadre has been approved.

      You now have access to:
      - View and participate in assigned panels
      - Access panel resources and documents
      - Collaborate with other cadre members

      Please log in to complete your profile and view available panels.

      Best regards,
      APEX Admin Team
    `;

    const result = await this.sendEmail(
      applicationData.email,
      subject,
      body,
      EMAIL_TYPES.APPLICATION_APPROVED
    );

    if (result.success) {
      toast.success('Approval notification sent');
    }

    return result;
  }

  /**
   * Send application rejection notification
   */
  async sendApplicationRejectedEmail(applicationData) {
    const subject = 'Application Update - APEX Panel System';
    const body = `
      Dear ${applicationData.applicantName},

      Thank you for your interest in joining the APEX Cadre.

      After careful review, we regret to inform you that your application has not been approved at this time.

      ${applicationData.rejectionReason ? `Reason: ${applicationData.rejectionReason}` : ''}

      You may reapply after addressing the feedback provided or gaining additional experience.

      Best regards,
      APEX Admin Team
    `;

    const result = await this.sendEmail(
      applicationData.email,
      subject,
      body,
      EMAIL_TYPES.APPLICATION_REJECTED
    );

    if (result.success) {
      toast.info('Rejection notification sent');
    }

    return result;
  }

  /**
   * Send panel assignment notification
   */
  async sendPanelAssignmentEmail(userData, panelData) {
    const subject = `Panel Assignment: ${panelData.title}`;
    const body = `
      Dear ${userData.name},

      You have been assigned to the following panel:

      Panel: ${panelData.title}
      Community: ${panelData.community}
      Role: ${userData.role || 'Panel Member'}
      Deadline: ${new Date(panelData.deadline).toLocaleDateString()}

      Please log in to view panel details and documents.

      Best regards,
      APEX Admin Team
    `;

    const result = await this.sendEmail(
      userData.email,
      subject,
      body,
      EMAIL_TYPES.PANEL_ASSIGNMENT
    );

    if (result.success) {
      toast.success('Assignment notification sent');
    }

    return result;
  }

  /**
   * Send panel status change notification
   */
  async sendPanelStatusChangeEmail(panelData, recipients) {
    const subject = `Panel Status Update: ${panelData.title}`;
    const body = `
      The status of panel "${panelData.title}" has been updated.

      New Status: ${panelData.status}
      Updated: ${new Date().toLocaleDateString()}

      ${panelData.status === 'COMPLETED' ? 'The panel has been completed. Final report will be available soon.' : ''}
      ${panelData.status === 'PUBLISHED' ? 'The panel has been published and is now publicly visible.' : ''}

      Please log in to view the latest updates.

      Best regards,
      APEX Admin Team
    `;

    const promises = recipients.map(recipient => 
      this.sendEmail(
        recipient.email,
        subject,
        body,
        EMAIL_TYPES.PANEL_STATUS_CHANGE
      )
    );

    const results = await Promise.all(promises);
    const successCount = results.filter(r => r.success).length;
    
    if (successCount > 0) {
      toast.success(`Status update sent to ${successCount} recipients`);
    }

    return { success: true, sent: successCount, total: recipients.length };
  }

  /**
   * Send shortlist review request
   */
  async sendShortlistReviewRequest(reviewerData, panelData, shortlistData) {
    const subject = `Action Required: Review Panel Shortlist`;
    const body = `
      Dear ${reviewerData.name},

      You have been requested to review the shortlist for the following panel:

      Panel: ${panelData.title}
      Community: ${panelData.community}
      Shortlisted Candidates: ${shortlistData.selectedCadre?.length || 0}

      As a ${reviewerData.role === 'COMMUNITY_MEMBER' ? 'Committee Member' : 'Client Representative'}, 
      your input is valuable in selecting the final panel members.

      Please log in to review the candidates and provide your feedback.

      Best regards,
      APEX Admin Team
    `;

    const result = await this.sendEmail(
      reviewerData.email,
      subject,
      body,
      EMAIL_TYPES.SHORTLIST_REVIEW_REQUEST
    );

    if (result.success) {
      toast.success('Review request sent');
    }

    return result;
  }

  /**
   * Send deadline reminder
   */
  async sendDeadlineReminder(userData, panelData, daysRemaining) {
    const subject = `Reminder: Panel Deadline Approaching`;
    const body = `
      Dear ${userData.name},

      This is a reminder that the deadline for the following panel is approaching:

      Panel: ${panelData.title}
      Deadline: ${new Date(panelData.deadline).toLocaleDateString()}
      Days Remaining: ${daysRemaining}

      Please ensure all required tasks are completed before the deadline.

      Best regards,
      APEX Admin Team
    `;

    return await this.sendEmail(
      userData.email,
      subject,
      body,
      EMAIL_TYPES.DEADLINE_REMINDER
    );
  }

  /**
   * Store notification in local storage (for demo)
   */
  storeNotification(notification) {
    try {
      const notifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
      notifications.unshift(notification);
      
      // Keep only last 100 notifications
      if (notifications.length > 100) {
        notifications.splice(100);
      }
      
      localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
      
      // Trigger custom event for real-time updates
      window.dispatchEvent(new CustomEvent('notificationAdded', { detail: notification }));
    } catch (error) {
      console.error('Failed to store notification:', error);
    }
  }

  /**
   * Get stored notifications
   */
  getStoredNotifications(limit = 50) {
    try {
      const notifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
      return notifications.slice(0, limit);
    } catch (error) {
      console.error('Failed to retrieve notifications:', error);
      return [];
    }
  }

  /**
   * Clear stored notifications
   */
  clearStoredNotifications() {
    localStorage.removeItem(NOTIFICATIONS_KEY);
  }

  /**
   * Get notification count
   */
  getUnreadCount() {
    const notifications = this.getStoredNotifications();
    return notifications.filter(n => !n.read).length;
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId) {
    try {
      const notifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
      const notification = notifications.find(n => n.id === notificationId);
      
      if (notification) {
        notification.read = true;
        localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
      }
      
      return { success: true };
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return { success: false, error };
    }
  }

  /**
   * Send bulk emails
   */
  async sendBulkEmails(recipients, subject, body, type = 'general') {
    const promises = recipients.map(recipient => 
      this.sendEmail(recipient.email || recipient, subject, body, type)
    );
    
    const results = await Promise.all(promises);
    const successCount = results.filter(r => r.success).length;
    
    toast.info(`Emails sent: ${successCount}/${recipients.length}`);
    
    return { 
      success: successCount > 0, 
      sent: successCount, 
      total: recipients.length,
      results 
    };
  }

  /**
   * Schedule a notification (mock implementation)
   */
  async scheduleNotification(notification, sendAt) {
    const delay = new Date(sendAt).getTime() - Date.now();
    
    if (delay > 0) {
      setTimeout(() => {
        this.sendEmail(
          notification.to,
          notification.subject,
          notification.body,
          notification.type
        );
      }, delay);
      
      return { success: true, message: `Notification scheduled for ${sendAt}` };
    }
    
    return { success: false, message: 'Invalid schedule time' };
  }
}

// Create singleton instance
const notificationService = new NotificationService();

// Export service
export default notificationService;

// Export for named imports
export {
  notificationService,
  EMAIL_TYPES
};
